/**
 * Workbench — turn materials into stims and gear.
 *
 * The recipe *copy* always comes from `RECIPES` in shared, so the numbers on screen are
 * the numbers the server will charge. `/api/craft` only decides which recipe ids are on
 * offer and what the Workbench module level is; if it is unreachable, every recipe at or
 * below level 1 is shown greyed with an honest reason rather than an empty tab.
 *
 * Phase 2c took the mods out — there are no weapon rails any more, so the "Upgrade slots" block
 * and its `ModRail` went with them — and put the whole tab behind `CRAFTING_ENABLED`, which is
 * `false` until the crafting economy is redesigned around the new item table. `App.tsx` hides the
 * tab; this guard is the second lock, so a stale deep link cannot reach a half-built bench.
 */

import type { VNode } from 'preact';
import { useState } from 'preact/hooks';
import {
  CRAFTING_ENABLED,
  ITEMS,
  RECIPES,
  recipeDurationSec,
  type ItemStack,
  type Recipe,
} from '@stillsun/shared';
import { fetchCraft, type CraftResponse } from '../../net/progress-api.js';
import { startCraftJob } from '../../net/economy-api.js';
import { session } from '../../net/session.js';
import { WorkbenchScene, countdown } from './WorkbenchScene.js';
import {
  LEDGER_SILENT,
  Loading,
  Notice,
  Section,
  errText,
  loadList,
  rarityHex,
  useLoader,
  wick,
} from './common.js';

export interface WorkbenchProps {
  nonce: number;
  token: string;
}

const nameOf = (itemId: string): string => ITEMS[itemId]?.name ?? itemId;

/** How many of `itemId` the player owns across the stash and everything carried. */
function countOf(items: readonly ItemStack[], itemId: string): number {
  let n = 0;
  for (const s of items) if (s.itemId === itemId) n += s.qty;
  return n;
}

export function Workbench(props: WorkbenchProps): VNode {
  const { token } = props;
  const offer = useLoader<CraftResponse>(
    () => fetchCraft(token),
    `craft:${String(props.nonce)}:${token}`,
  );
  const stash = useLoader<ItemStack[]>(
    () => loadList<ItemStack>('/api/stash', token, 'items'),
    `craftstash:${String(props.nonce)}:${token}`,
  );
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const [done, setDone] = useState('');

  if (!CRAFTING_ENABLED) {
    return (
      <div class="hub-pane">
        <Notice>
          The bench is cold. Whatever the Compact used to let you make here is being re-cut for the
          new lines — come back when the smith does.
        </Notice>
      </div>
    );
  }

  if (offer.loading || stash.loading) {
    return (
      <div class="hub-pane">
        <Loading />
      </div>
    );
  }

  const level = offer.data?.workbenchLevel ?? 1;
  const items = stash.data ?? [];
  const wickHeld = session.player?.wick ?? 0;
  // A server that answered tells us exactly what it will craft; one that did not gets the
  // benefit of the doubt at Workbench level 1.
  const offered = offer.data
    ? new Set(offer.data.recipeIds)
    : new Set(
        Object.values(RECIPES)
          .filter((r) => r.level <= 1)
          .map((r) => r.id),
      );

  const list = (Object.values(RECIPES) as Recipe[]).sort(
    (a, b) => a.level - b.level || a.name.localeCompare(b.name),
  );

  /**
   * M6: a recipe is put *on the bench*, not minted. `startCraftJob` takes the materials and the
   * fee now and queues the job; the scene above hears about it through `onBenchChange` and starts
   * drawing it, and the output only reaches the stash when the player collects it up there. There
   * is exactly one crafting path in the client and one on the server.
   */
  const make = (recipe: Recipe): void => {
    if (busy.length > 0) return;
    setBusy(recipe.id);
    setNote('');
    setDone('');
    void (async () => {
      try {
        await startCraftJob(token, recipe.id);
        setDone(
          `${nameOf(recipe.output.itemId)} — on the bench. ${countdown(recipeDurationSec(recipe.id))} to go.`,
        );
        stash.reload();
        // The fee comes out of Wick, so the header is stale until the profile reloads.
        await session.refresh();
      } catch (err) {
        setNote(errText(err, 'The bench refused that one.'));
      } finally {
        setBusy('');
      }
    })();
  };

  return (
    <div class="hub-pane">
      {/* The bench itself — the drawing, the job queue and the countdown. Standalone by
          construction (its own fetch against /api/craft/jobs), so nothing below depends on it. */}
      <WorkbenchScene nonce={props.nonce} token={token} />

      {offer.data === null ? <p class="hub-notice">{LEDGER_SILENT}</p> : null}
      {note.length > 0 ? <p class="hub-error">{note}</p> : null}
      {done.length > 0 ? <p class="hub-done-line">{done}</p> : null}

      <Section title="Workbench" hint={`Module level ${String(level)}`}>
        <div class="hub-recipes">
          {list.map((r) => {
            const outDef = ITEMS[r.output.itemId];
            const locked = r.level > level;
            const notOffered = !offered.has(r.id) && !locked;
            const haveAll = r.inputs.every((i) => countOf(items, i.itemId) >= i.qty);
            const affordable = wickHeld >= r.fee;
            const can = !locked && !notOffered && haveAll && affordable && stash.data !== null;
            return (
              <article key={r.id} class={`hub-card hub-recipe${locked ? ' locked' : ''}`}>
                <h3
                  class="hub-card-title"
                  style={outDef ? { color: rarityHex(outDef.rarity) } : {}}
                >
                  {r.name}
                </h3>
                <p class="hub-card-blurb">
                  {`Makes ${nameOf(r.output.itemId)}${r.output.qty > 1 ? ` ×${String(r.output.qty)}` : ''}`}
                  {outDef ? ` — ${outDef.desc}` : ''}
                </p>
                <ul class="hub-inputs">
                  {r.inputs.map((i) => {
                    const have = countOf(items, i.itemId);
                    return (
                      <li key={i.itemId} class={have >= i.qty ? 'ok' : 'short'}>
                        <span class="n">{nameOf(i.itemId)}</span>
                        <span class="c">{`${String(Math.min(have, i.qty))} / ${String(i.qty)}`}</span>
                      </li>
                    );
                  })}
                </ul>
                <div class="hub-card-foot">
                  <span class={affordable ? 'hub-price' : 'hub-price short'}>
                    {wick(r.fee)} Wick fee
                  </span>
                  {locked ? (
                    <span class="hub-hint">{`Needs Workbench ${String(r.level)}`}</span>
                  ) : notOffered ? (
                    <span class="hub-hint">Not on this bench yet</span>
                  ) : null}
                  <button
                    type="button"
                    class="hub-btn hub-btn-amber"
                    disabled={!can || busy.length > 0}
                    onClick={() => {
                      make(r);
                    }}
                  >
                    {busy === r.id ? 'Working…' : `Bench · ${countdown(recipeDurationSec(r.id))}`}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </Section>
    </div>
  );
}
